import { useEffect } from 'react'
import { CircleCheck, CircleX, Info, TriangleAlert, X } from 'lucide-react'
import { Icon } from '../icons/Icon'
import { Button } from './Button'
import './toast.css'

export type ToastKind = 'success' | 'error' | 'warning' | 'info'

/** One control, so a toast is never a dead end when the outcome is reversible. */
export type ToastAction = { label: string; onAct: () => void }

/**
 * How long each kind sits there.
 *
 * Not one number: an error names whose fault it is and the recovery path —
 * CLAUDE.md's bar — which is materially more to read than "Saved." A success
 * that lingers is clutter; an error that vanishes mid-sentence is a defect.
 *
 * Exported so the tests can advance a fake clock past a duration instead of
 * hard-coding a second copy of it, which is the drift this repo keeps paying
 * for elsewhere.
 */
export const TOAST_MS: Record<ToastKind, number> = {
  success: 5000,
  info: 5000,
  warning: 8000,
  error: 8000,
}

/**
 * How long a toast that OFFERS something sits there.
 *
 * **A long clock, and it replaces two worse answers.** Measured: `Undo` on
 * `/overview` is 27 tab stops from the top of the document, because the queue's
 * host is the last child of `<body>`. Against a 5000ms success clock the
 * affordance is pointer-only, which CLAUDE.md rules out.
 *
 * Not retiring at all fixed that and bought a worse problem, also measured:
 * three actionable toasts occupy 39% of a 320x568 viewport indefinitely, with
 * dismissal the only exit. At roughly two Tab presses a second, twenty seconds
 * covers those 27 stops with room to spare — and the moment focus lands, the
 * pause holds the toast for as long as the user needs it. Reachable AND
 * bounded, which neither of the other two answers was.
 *
 * The real fix is a keyboard route TO the queue rather than a longer clock;
 * that is #216.
 */
export const ACTIONABLE_MS = 20000

/**
 * The mark, which is what makes the kind legible without colour.
 *
 * CLAUDE.md: "Colour is never the only signal." Four toasts differing only in
 * `border-color` are one toast to a colour-blind user. The glyphs differ in
 * SHAPE — circle-tick, circle-cross, triangle, circle-i — not merely in fill.
 *
 * `aria-hidden` by default (see `Icon`): the message beside it already says
 * what happened, and "image, alert, Royal Bank hasn't synced" reads the state
 * twice.
 */
const MARK = {
  success: CircleCheck,
  error: CircleX,
  warning: TriangleAlert,
  info: Info,
} as const

type Props = {
  /** Passed back to `onDismiss` so the callback can stay stable — see below. */
  id: string
  kind: ToastKind
  message: string
  action?: ToastAction
  /**
   * Held by the QUEUE, not by this box — a pointer over any toast pauses the
   * whole stack. Per-toast, hovering the second one let the first and third
   * retire under the cursor, which is the opposite of what a reader wants.
   * It costs one prop.
   */
  paused: boolean
  /**
   * Whether the dismissal came from something other than a pointer, which is
   * what decides where focus goes next — see `useStackFocus`.
   *
   * Read as `event.detail === 0` at the call sites below. Measured in Chrome:
   * Enter and Space on a button give `detail: 0`, a mouse click gives 1, and a
   * touch tap under emulation also gives 1 — so a phone tap correctly does not
   * get the keyboard affordance.
   *
   * **It is broader than "keyboard", deliberately.** A programmatic `.click()`
   * and most assistive-technology activations also report 0, so they get the
   * onward walk too. That is the right answer for a screen-reader user driving
   * the queue, and it is why this is named for what it is NOT — a pointer —
   * rather than for the one input that motivated it.
   */
  onDismiss: (id: string, notFromPointer?: boolean) => void
}

/**
 * One toast: the box, its mark, and its own retirement clock.
 *
 * **The clock lives here and the PAUSE lives in the queue**, which is the split
 * both reviewers landed on. A timeout belongs beside the thing it retires; a
 * pause is a property of the stack the user is reading, not of one box.
 *
 * **`onDismiss` takes the id.** A `() => dismiss(t.id)` closure is a new
 * function on every provider render, and this component restarts its timeout
 * whenever that identity changes — so a toast beside a toast that arrives or
 * retires would silently have its clock reset and could outlive its duration
 * indefinitely. With the id as an argument the provider hands down one stable
 * `useCallback` and the effect below depends on nothing that churns.
 *
 * **A toast carrying an action gets `ACTIONABLE_MS`** — see the note there for
 * why a long clock beats both the 5000ms it replaced and the never-retire that
 * replaced that.
 *
 * The skin is in `toast.css`. Nothing here is inline: a fill written in a style
 * object beats every selector, so a `:hover` rule for it could never fire —
 * the failure `primitives.css` records having shipped three times.
 */
export function Toast({ id, kind, message, action, paused, onDismiss }: Props) {
  const actionable = action !== undefined

  useEffect(() => {
    if (paused) return
    // Restarts the FULL duration on unpause rather than resuming the remainder.
    // Someone who hovered to read it gets to finish reading it after they move
    // away, which is the behaviour the pause exists for in the first place.
    const timer = window.setTimeout(() => onDismiss(id), actionable ? ACTIONABLE_MS : TOAST_MS[kind])
    return () => window.clearTimeout(timer)
  }, [paused, actionable, kind, id, onDismiss])

  return (
    <div
      // `enter` is motion.css's 280ms/6px rise, and it is how this participates
      // in the one prefers-reduced-motion block. A local keyframe would be
      // invisible to it — M0-R20.
      className={`toast toast--${kind} enter`}
      // How the queue finds this box's neighbour when focus has to be moved off
      // a control that is about to be unmounted — see `ToastProvider`'s
      // `focusOnDismiss`. Pointer and focus PAUSE are handled on the container,
      // not here, so that hovering one toast holds the whole stack.
      data-toast={id}
    >
      <span className="toast__mark">
        <Icon glyph={MARK[kind]} />
      </span>
      <p className="toast__message">{message}</p>
      <div className="toast__controls">
        {action && (
          // `outline`, not `primary`: --accent may fill exactly one element in
          // the page body, and a toast is a report rather than the page's
          // primary action.
          <Button
            variant="outline"
            // `page` (33px), not `panel` (30px). A toast floats over the page
            // rather than sitting inside a panel, and `hitTarget.ts` documents
            // the smaller targets as being for dense in-panel and in-row work.
            // M0-R29's 44px mobile floor is #90's — no size in the map reaches
            // it, so 33px is what this measures at 320px.
            size="page"
            onClick={(event) => {
              action.onAct()
              // Leaving it up offers an Undo for something already undone.
              onDismiss(id, event.detail === 0)
            }}
          >
            {action.label}
          </Button>
        )}
        {/* A `Button`, deliberately: the disabled-state guard in
            controlTokens.test.ts fails ANY stylesheet carrying a disabled
            spelling that is not a registered control sheet, exemption or not.
            Skinning a bare <button> here would mean either a second control
            sheet or a control with no disabled affordance at all. */}
        <Button
          variant="chip"
          size="page"
          aria-label="Dismiss"
          data-toast-dismiss=""
          onClick={(event) => onDismiss(id, event.detail === 0)}
        >
          <Icon glyph={X} />
        </Button>
      </div>
    </div>
  )
}
